import { useCallback, useEffect, useState } from 'react';
import { Badge, StatusPip, Table } from '../components/ui';
import { EmptyStateBlock, ErrorState, LoadingState } from '../components/states';
import { useSession } from '../app/session';
import { listDevices, subscribeDevices, type DeviceRecord } from '../api/devices';
import { listSubmissions, subscribeSubmissions, submissionSummary, type SubmissionRecord } from '../api/submissions';
import { pb } from '../api/pocketbase';

type DeploymentRecord = { id: string; deployed_at?: string; status?: string; version?: number };

const NEVER_SENTINEL = '1970-01-01T00:00:00.000Z';

function isNeverSeen(device: DeviceRecord): boolean {
  return !device.last_seen || device.last_seen.startsWith('1970');
}

function relativeTime(iso: string | undefined): string {
  if (!iso || iso.startsWith(NEVER_SENTINEL.slice(0, 4))) return 'never';
  const seconds = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  return `${hours}h ago`;
}

export function Overview() {
  const { session } = useSession();
  const [devices, setDevices] = useState<DeviceRecord[]>([]);
  const [submissions, setSubmissions] = useState<SubmissionRecord[]>([]);
  const [deployments, setDeployments] = useState<DeploymentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const [deviceList, submissionList, deploymentList] = await Promise.all([
        listDevices(),
        listSubmissions(),
        pb.collection('deployments').getList(1, 5, { sort: '-deployed_at' }).then((result) => result.items as DeploymentRecord[])
      ]);
      setDevices(deviceList);
      setSubmissions(submissionList.slice(0, 5));
      setDeployments(deploymentList);
      setError('');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not load the overview.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => subscribeDevices(setDevices), []);
  useEffect(() => subscribeSubmissions(() => void load()), [load]);

  const online = devices.filter((device) => device.status === 'online').length;
  const offline = devices.filter((device) => device.status === 'offline').length;
  const neverConnected = devices.filter(isNeverSeen).length;
  const revoked = devices.filter((device) => device.status === 'revoked');
  const offlineDevices = devices.filter((device) => device.status === 'offline');
  const notReporting = [...new Set([...revoked, ...offlineDevices])];

  const metrics = [
    { label: 'Devices / total', value: String(devices.length), detail: `${session?.tenant.name ?? ''}`.trim() || 'This tenant' },
    { label: 'Devices / online', value: String(online), detail: `${offline} offline` },
    { label: 'Never connected', value: String(neverConnected), detail: 'Awaiting first heartbeat' },
    { label: 'Submissions', value: String(submissions.length), detail: 'Most recent 5 shown' }
  ];

  return (
    <main className="workspace-main">
      <header className="page-header">
        <div>
          <span className="index-label">CONTROL ROOM / OVERVIEW</span>
          <h1>Screen operations</h1>
        </div>
      </header>

      {loading && <LoadingState label="Loading overview" rows={2} />}
      {error && <ErrorState title="Overview unavailable" detail={error} onRetry={() => void load()} />}

      {!loading && !error && (
        <>
          <section className="metric-row" aria-label="Fleet status">
            {metrics.map((metric) => (
              <div className="metric" key={metric.label}>
                <span className="index-label">{metric.label.toUpperCase()}</span>
                <strong>{metric.value}</strong>
                <span>{metric.detail}</span>
              </div>
            ))}
          </section>

          {(offlineDevices.length > 0 || revoked.length > 0) && (
            <section className="panel" aria-label="Alerts">
              <header className="panel-heading">
                <span className="index-label">ALERTS / {String(offlineDevices.length + revoked.length).padStart(2, '0')}</span>
              </header>
              <div className="panel-body">
                <ul className="audit-list">
                  {offlineDevices.map((device) => (
                    <li key={device.id}>
                      <StatusPip status="warn" label="Offline" /> {device.name ?? device.id} — last seen {relativeTime(device.last_seen)}
                    </li>
                  ))}
                  {revoked.map((device) => (
                    <li key={device.id}>
                      <StatusPip status="danger" label="Revoked" /> {device.name ?? device.id} — returns to pairing
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          )}

          <section className="panel">
            <header className="panel-heading">
              <span className="index-label">SUBMISSIONS / RECENT</span>
            </header>
            {submissions.length === 0 ? (
              <div className="panel-body">
                <EmptyStateBlock title="No submissions yet" detail="Check-in submissions appear here live." />
              </div>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Summary</th>
                    <th>Device</th>
                  </tr>
                </thead>
                <tbody>
                  {submissions.map((row) => (
                    <tr key={row.id}>
                      <td className="mono">{relativeTime(row.submitted_at)}</td>
                      <td>{submissionSummary(row)}</td>
                      <td>{row.expand?.device?.name ?? row.device ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </section>

          <section className="panel">
            <header className="panel-heading">
              <span className="index-label">DEPLOYMENTS / RECENT</span>
            </header>
            {deployments.length === 0 ? (
              <div className="panel-body">
                <EmptyStateBlock title="No deployments yet" detail="Deploy a layout to a device to see it here." />
              </div>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <th>Deployed</th>
                    <th>Version</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {deployments.map((deployment) => (
                    <tr key={deployment.id}>
                      <td className="mono">{relativeTime(deployment.deployed_at)}</td>
                      <td>v{deployment.version ?? 1}</td>
                      <td>
                        <Badge>{deployment.status ?? 'unknown'}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </section>
        </>
      )}
    </main>
  );
}
